// Filmistani Pro subscription context.
// Single source of truth for tier + daily quota state on the frontend.
// v6.5 — Pro Mode kill switch: fetches /api/system/settings and overrides
//         isPro=true for ALL users when pro_mode_enabled is false.
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "./api";
import { useAuth } from "./AuthContext";

const SubCtx = createContext({
  loading: true,
  tier: "guest",
  isPro: false,
  proModeEnabled: true,
  subscription: null,
  usageToday: { count: 0, limit: 5, remaining: 5 },
  trialOffer: { eligible: false },
  founderSlotsRemaining: 200,
  refresh: async () => {},
  paywallOpen: false,
  openPaywall: () => {},
  closePaywall: () => {},
});

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState({
    loading: true,
    tier: "guest",
    isPro: false,
    proModeEnabled: true,
    subscription: null,
    usageToday: { count: 0, limit: 5, remaining: 5 },
    trialOffer: { eligible: false },
    founderSlotsRemaining: 200,
  });
  const [paywallOpen, setPaywallOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [subRes, sysRes] = await Promise.allSettled([
        api.get("/subscription/me"),
        api.get("/system/settings"),
      ]);

      const sub = subRes.status === "fulfilled" ? subRes.value.data : {};
      const sys = sysRes.status === "fulfilled" ? sysRes.value.data : {};

      const proModeEnabled = sys?.pro_mode_enabled !== false; // default true

      // Kill switch: when pro mode is OFF treat every user as Pro
      const isPro = !proModeEnabled ? true : !!sub?.is_pro;
      const tier  = !proModeEnabled
        ? (sub?.tier && sub?.tier !== "guest" ? sub.tier : "pro")
        : (sub?.tier || "guest");

      setState({
        loading: false,
        tier,
        isPro,
        proModeEnabled,
        subscription:          sub?.subscription            || null,
        usageToday:            sub?.usage_today             || { count: 0, limit: 5, remaining: 5 },
        trialOffer:            sub?.trial_offer             || { eligible: false },
        founderSlotsRemaining: sub?.founder_slots_remaining ?? 200,
      });
    } catch (_e) {
      setState((s) => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh, user?.user_id]);

  // Global "open paywall" event so deep components can trigger the modal
  // without prop-drilling.
  useEffect(() => {
    const handler = () => setPaywallOpen(true);
    window.addEventListener("fm-open-paywall", handler);
    return () => window.removeEventListener("fm-open-paywall", handler);
  }, []);

  const openPaywall = () => {
    // When pro mode is off, never show the paywall.
    if (!state.proModeEnabled) return;
    setPaywallOpen(true);
    api.post("/subscription/paywall-event", { kind: "trial_offered" }).catch(() => {});
  };

  const closePaywall = () => {
    setPaywallOpen(false);
    api.post("/subscription/paywall-event", { kind: "dismiss" }).catch(() => {});
  };

  return (
    <SubCtx.Provider
      value={{
        ...state,
        refresh,
        paywallOpen,
        openPaywall,
        closePaywall,
      }}
    >
      {children}
    </SubCtx.Provider>
  );
}

export const useSubscription = () => useContext(SubCtx);
